import { registerLocaleData } from '@angular/common';
import localeNL from '@angular/common/locales/nl';
import { ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { firstValueFrom } from 'rxjs';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { ExternalModule } from './external.module';
import { MainModule } from './modules/main/main.module';
import { MollieModule } from './modules/mollie-redirect/mollie.module';
import { AuthService } from './services/auth/auth.service';
import { FeatureFlagsService } from './services/feature-flags/feature-flags.service';
import { SharedModule } from './shared/shared.module';

registerLocaleData(localeNL, 'nl');

@NgModule({
  declarations: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    CoreModule,
    MainModule,
    ExternalModule,
    SharedModule,
    MollieModule,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      // Eagerly instantiate the Sentry TraceService so router instrumentation
      // is wired up before the app renders.
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => {
      // The session token lives in memory only (StorageService) and is lost on a hard
      // reload. Without this, synchronous auth checks (e.g. AuthService.isUserSuperAdmin())
      // read an empty token until some component's HTTP call happens to 401 and the
      // interceptor reactively refreshes it — too late for state already built in a
      // constructor/ngOnInit. Proactively restore the token from the httpOnly refresh
      // cookie before the app renders; falls through to "logged out" if there's no
      // valid cookie.
      const auth = inject(AuthService);
      const flags = inject(FeatureFlagsService);
      return (async () => {
        try {
          const token: any = await firstValueFrom(auth.renewToken());
          auth.storeToken(token.accessToken);
        } catch {
          // No valid session/refresh cookie — proceed as logged out.
        }
        if (auth.isLoggedIn().login) {
          await firstValueFrom(flags.loadSafe());
        }
      })();
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
